import { useState, useEffect, useCallback } from 'react';
import {
  listPendingAssociations,
  approvePendingAssociation,
  rejectPendingAssociation,
} from '../api/client.js';
import type { PendingAssociation } from '../api/client.js';

export function AdminAssociationsPage() {
  const [rows, setRows] = useState<PendingAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await listPendingAssociations();
      setRows(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id: string) => {
    setActionId(id);
    try {
      await approvePendingAssociation(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionId(id);
    try {
      await rejectPendingAssociation(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } finally {
      setActionId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
      <h1 className="mb-6 text-2xl font-bold text-gray-100">Pending Associations</h1>

      {rows.length === 0 ? (
        <p className="text-gray-400">No pending associations to review.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-800 bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Requester</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Quote</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Source</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Match</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Date</th>
                <th className="px-4 py-3 text-right font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-900/30">
                  <td className="px-4 py-3 text-gray-200">
                    <div>{row.requesterUserEmail}</div>
                    {row.requesterUserPhone && (
                      <div className="text-xs text-gray-500">{row.requesterUserPhone}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {row.quoteDisplayId || row.quoteId.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{row.sourceTenantName}</td>
                  <td className="px-4 py-3">
                    <span className="rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-300">
                      {row.matchType}
                    </span>{' '}
                    <span className="text-gray-400">{row.matchValue}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {new Date(row.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleApprove(row.id)}
                      disabled={actionId === row.id}
                      className="mr-2 rounded bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(row.id)}
                      disabled={actionId === row.id}
                      className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
